import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { usePageTitle } from '../config';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CardMedia from '@mui/material/CardMedia';
import { styled } from '@mui/material/styles';
import { Footer } from '@im/ui-lib/Footer';
import { IngramLogo } from '@im/ui-lib/icons/IngramLogo';
import { useUserInfo } from './user-info.hooks';
import { CulturePicker } from '@im/ui-lib/CulturePicker/CulturePicker';
import { useTranslate } from '@im/react-localization';
import { CookiesConsent } from '../content';
import { PageLoadTimeProvider } from '../provider/dd-custom-actions';
import { useWelcomeVideoURL } from './useWelcomeVideoURL';
import { useCopyrightLinks } from './useCopyrightLinks';

export const WelcomeVideo = () => {
  const navigate = useNavigate();
  const userInfo = useUserInfo();
  const copyRightLinks = useCopyrightLinks();
  const { data: dynamicContent, isLoading } = useWelcomeVideoURL();

  const translate = useTranslate();
  const { updatePageTitle } = usePageTitle();
  updatePageTitle(translate('Title.Welcome'));
  const [onetrustEnabled, setOnetrustEnabled] = useState(true);
  const [onetrustUpdated, setOnetrustUpdated] = useState(0);

  useEffect(() => {
    const win = window as any;
    const isConsented = win?.OnetrustActiveGroups
      ? ['C0002', 'C0003', 'C0004'].every((x) =>
          win.OnetrustActiveGroups.includes(x)
        )
      : false;
    setOnetrustEnabled(!!win?.isOneTrustEnabled && isConsented);
  }, [onetrustUpdated]);

  useEffect(() => {
    const eventFunction = () => {
      setOnetrustUpdated((x) => x + 1);
    };
    window.addEventListener('bannerLoaded', eventFunction);
    return () => {
      window.removeEventListener('bannerLoaded', eventFunction);
    };
  }, []);

  return (
    <PageLoadTimeProvider>
      <ScreenContainer>
        <HeaderContainer>
          <IngramLogo />
          <CulturePicker />
        </HeaderContainer>
        <MainContainer>
          <PlayerContainer>
            {onetrustEnabled ? (
              !isLoading &&
              (dynamicContent && dynamicContent.videos?.length > 0 ? (
                <CardMedia
                  component="iframe"
                  height="482"
                  image={
                    dynamicContent.videos[0].videoType === 'File'
                      ? dynamicContent.videos[0].videoFile
                      : dynamicContent.videos[0].embedCode
                  }
                />
              ) : (
                <CardMedia
                  component="iframe"
                  height="482"
                  image="https://www.youtube.com/embed/4CuBIiXiwtk"
                />
              ))
            ) : (
              <CookiesConsent height="479px"></CookiesConsent>
            )}
          </PlayerContainer>
          <RightContainer>
            <TextContainer>
              <WelcomeText>
                {translate('WelcomeVideo.Welcome')}, {`${userInfo?.given_name}`}
                !
              </WelcomeText>
              <Text>{translate('WelcomeVideo.DigitalTwin')}</Text>
              <Text>{translate('WelcomeVideo.Description')}</Text>
              <Text>{translate('WelcomeVideo.PrivacyPolicy')}</Text>
            </TextContainer>
            <StyledButton
              data-testid="LetsBegin"
              onClick={() => navigate('/my/dashboard')}
              variant="contained"
            >
              {translate('WelcomeVideo.Begin')}
            </StyledButton>
          </RightContainer>
        </MainContainer>
        <FooterContainer>
          <Footer copyRightLinks={copyRightLinks} />
        </FooterContainer>
      </ScreenContainer>
    </PageLoadTimeProvider>
  );
};

const ScreenContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
}));

const HeaderContainer = styled('div')(({ theme }) => ({
  alignItems: 'center',
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  justifyContent: 'space-between',
  padding: theme.spacing(4, 4),
}));

const MainContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  marginLeft: 88,
  marginRight: 88,
  marginTop: 36,
}));

const PlayerContainer = styled('div')(({ theme }) => ({
  borderRadius: 6,
  flex: 1,
  height: 482,
  width: 856,
}));

const RightContainer = styled('div')(({ theme }) => ({
  flex: 1,
  marginLeft: 32,
}));

const TextContainer = styled('div')(({ theme }) => ({}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  fontSize: 24,
  fontWeight: 600,
}));

const Text = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 400,
  marginTop: 24,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: 40,
}));

const FooterContainer = styled('div')(({ theme }) => ({
  marginTop: 'auto',
}));
